// @flow
import React, { PureComponent, Fragment } from 'react';
import _ from 'lodash';
import { withHelmet } from 'infrastructure/seo/Helmet';

import Breadcrumb from 'components/fragments/Breadcrumb';
import EventListNavbar from 'components/event/EventListNavbar';
import EventListRow from 'components/event/EventListRow';
import EventListPagination from 'components/event/EventListPagination';
import Loading from 'components/fragments/Loading';

import { STATUS_SUCCESS } from 'constants/statusConstants';
import {
  LABEL_CALENDAR_TEAM,
  LABEL_CALENDAR_INSTITUTIONNEL,
  FILTER_COMPETITION,
  FILTER_TRAINING,
  FILTER_INSTITUTIONNEL,
  FILTER_COMPETITION_AMATEUR,
} from 'constants/eventConstants';

import { sortEventsByStartDate, isEventInCalendarTeam } from 'utils/eventUtils';
import { getPropertyAsFilterOptions } from 'utils/arrayUtils';
import { getPublicAssets } from 'utils/assetsUtils';
import { getMonthName } from 'utils/dateUtils';
import { getMetaBySlug } from 'utils/seoUtils';

import type { EventType, EventMatchType } from 'types/Event';
import type { Status } from 'types/Status';

export type StateProps = {
  events: Array<EventType>,
  eventsStatus: Status,
  matchEventsStatus: Status,
  calendarTeamIds: Array<string>,
  matchEvents: Array<EventMatchType>
};

export type DispatchProps = {
  fetchEvents: (from: Date, to: Date) => void,
  fetchMatchEvents: (from: Date, to: Date) => void
};

type Props = {
  match: {
    params: {
      month?: string,
      year?: string
    }
  },
  history: any
} & StateProps &
  DispatchProps;

type State = {
  currentMonth: Date,
  label: string,
  teamFilters: Array<{ value: string, label: string }>,
  eventFilters: Array<{ value: string, label: string }>,
  federalFilters: Array<{value: string, label: string}>,
  selectedEventFilters: Array<string>,
  selectedFederalFilters: Array<string>,
  selectedTeamFilters: Array<string>,
};

class EventList extends PureComponent<Props, State> {
  props: Props;
  state: State = {
    currentMonth: new Date(),
    label: LABEL_CALENDAR_TEAM,
    teamFilters: [],
    eventFilters: [
      { value: FILTER_COMPETITION, label: 'Compétitions' },
      { value: FILTER_TRAINING, label: 'Entraînements' }
    ],
    federalFilters: [
      { value: FILTER_COMPETITION_AMATEUR, label: 'Compétitions amateurs' },
      { value: FILTER_INSTITUTIONNEL, label: 'Institutionnel' }
    ],
    selectedEventFilters: [FILTER_COMPETITION, FILTER_TRAINING],
    selectedFederalFilters: [FILTER_INSTITUTIONNEL, FILTER_COMPETITION_AMATEUR],
    selectedTeamFilters: [],
  };

  componentDidMount() {
    const { month, year } = this.props.match.params;
    if (month && year) {
      let curMonth;
      curMonth = decodeURI(month);
      curMonth =
        'janvier___février___mars______avril_____mai_______juin______juillet___août______septembre_octobre___novembre__décembre__'.indexOf(
          curMonth
        ) / 10 + 1;
      curMonth = new Date(`${year}-0${curMonth}-01`);

      if (isNaN(curMonth.getTime())) {
        curMonth = new Date();
      }
      this.setState({ currentMonth: curMonth });
      this.updateEvents(curMonth);
    } else {
      this.updateEvents(this.state.currentMonth);
    }
  }

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    const { eventsStatus, matchEventsStatus, matchEvents, events, calendarTeamIds } = nextProps;

    if (eventsStatus === STATUS_SUCCESS && matchEventsStatus === STATUS_SUCCESS && calendarTeamIds) {
      const eventsTitle = [
        ...matchEvents.map(match => match.title),
        ...events.filter((event) => isEventInCalendarTeam(calendarTeamIds, event)).map(match => match.title)
      ];

      if (eventsTitle.length > 0 && calendarTeamIds.length > 0) {
        const eventsInTeam = [
          ...matchEvents,
          ...events.filter((event) => isEventInCalendarTeam(calendarTeamIds, event))
        ];
        const teamFilters = getPropertyAsFilterOptions(eventsInTeam, 'team');
        const selectedTeamFilters = teamFilters.map(teamFilter => teamFilter.value);

        if (teamFilters.length > 0 && !_.isEqual(teamFilters, prevState.teamFilters)) {
          return {
            ...prevState,
            teamFilters,
            selectedTeamFilters
          };
        } else {
          return {
            ...prevState,
            teamFilters,
          };
        }
      }

      return {
        ...prevState,
        selectedTeamFilters: [],
        teamFilters: [],
      };
    }

    return null;
  }

  updateEvents(date: Date) {
    const currentYear = date.getFullYear();
    const currentMonth = date.getMonth();
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);

    this.props.fetchEvents(firstDay, lastDay);
    this.props.fetchMatchEvents(firstDay, lastDay);
  }

  updateCurrentMonth = (currentMonth: Date) => {
    this.updateEvents(currentMonth);
    this.setState({ currentMonth });
  };

  handleIncrementCurrentMonth = (e: MouseEvent) => {
    e.preventDefault();
    const date = this.state.currentMonth;
    date.setMonth(date.getMonth() + 1);
    const month = getMonthName(date.getMonth());
    this.props.history.push(`/evenements/${date.getFullYear()}/${encodeURIComponent(month.toLowerCase())}`);
    this.updateCurrentMonth(new Date(date));
  };

  handleDecrementCurrentMonth = (e: MouseEvent) => {
    e.preventDefault();
    const date = this.state.currentMonth;
    date.setMonth(date.getMonth() - 1);
    const month = getMonthName(date.getMonth());
    this.props.history.push(`/evenements/${date.getFullYear()}/${encodeURI(month.toLowerCase())}`);
    this.updateCurrentMonth(new Date(date));
  };

  handleCurrentMonthChanged = (date: string) => {
    const currentMonth = new Date(date);
    const month = getMonthName(currentMonth.getMonth());
    this.props.history.push(`/evenements/${currentMonth.getFullYear()}/${encodeURI(month.toLowerCase())}`);
    this.updateCurrentMonth(currentMonth);
  };

  handleResetFilters = () => {
    this.setState({ selectedEventFilters: [], selectedTeamFilters: [] });
  };

  setLabel = (label: string) => {
    this.setState({ label });
  };

  handleEventFilterClicked = filter => {
    this.setState(state => {
      if (state.selectedEventFilters.includes(filter))
        return { selectedEventFilters: state.selectedEventFilters.filter(event => event !== filter) };
      else return { selectedEventFilters: [...state.selectedEventFilters, filter] };
    });
  };

  handleTeamFilterClicked = filter => {
    this.setState(state => {
      if (state.selectedTeamFilters.includes(filter))
        return { selectedTeamFilters: state.selectedTeamFilters.filter(event => event !== filter) };
      else return { selectedTeamFilters: [...state.selectedTeamFilters, filter] };
    });
  };

  handleFederalFilterClicked = filter => {
    this.setState(state => {
      if (state.selectedFederalFilters.includes(filter))
        return { selectedFederalFilters: state.selectedFederalFilters.filter(event => event !== filter) };
      else return { selectedFederalFilters: [...state.selectedFederalFilters, filter] };
    });
  };

  sortEvents = (events: Array<EventType | EventMatchType>, label: string): any => {
    const { calendarTeamIds } = this.props;
    const eventsWithLabel = events.filter(
      event =>
        (label === LABEL_CALENDAR_TEAM && isEventInCalendarTeam(calendarTeamIds, event)) ||
        (label !== LABEL_CALENDAR_TEAM && !isEventInCalendarTeam(calendarTeamIds, event))
    );

    let filteredEvents = label === LABEL_CALENDAR_TEAM ? this.filterTeamEvents(eventsWithLabel) : eventsWithLabel;
    filteredEvents = label !== LABEL_CALENDAR_TEAM ? this.filterFederalEvents(filteredEvents) : filteredEvents;

    if (filteredEvents.length > 0) {
      return sortEventsByStartDate(filteredEvents).map((el, index) => <EventListRow key={index} events={el.events} />);
    }

    return (
      <div className="placeholder ">
        <p className="ft-h5">
          <i className="icon icon-calendar-empty"></i>
          <br/>Aucun événement pour cette recherche
        </p>
      </div>
    );
  };

  filterTeamEvents = (events: Array<EventType | EventMatchType>): Array<EventType | EventMatchType> => {
    const { selectedEventFilters, selectedTeamFilters } = this.state;
    let filteredEvents: Array<any> = events;

    if (!selectedEventFilters.includes(FILTER_COMPETITION)) {
      filteredEvents = filteredEvents.filter(event => !event.adversaire);
    }

    if (!selectedEventFilters.includes(FILTER_TRAINING)) {
      filteredEvents = filteredEvents.filter(event => event.adversaire);
    }

    return filteredEvents.filter(event => selectedTeamFilters.includes(event.team));
  };

  filterFederalEvents = (events: Array<EventType | EventMatchType>): Array<EventType | EventMatchType> => {
    const { selectedFederalFilters } = this.state;
    let filteredEvents: Array<EventType | EventMatchType> = events;

    if (!selectedFederalFilters.includes(FILTER_COMPETITION_AMATEUR)) {
      filteredEvents = filteredEvents.filter(event =>
        event.categories && Array.isArray(event.categories)?
          event.categories.some((category) => category.slug === FILTER_INSTITUTIONNEL)
          : false
      );
    }

    if (!selectedFederalFilters.includes(FILTER_INSTITUTIONNEL)) {
      filteredEvents = filteredEvents.filter(event =>
        event.categories && Array.isArray(event.categories) ?
          event.categories.some((category) => category.slug === FILTER_COMPETITION_AMATEUR)
          : false
      );
    }

    return filteredEvents;
  };

  renderEvents = () => {
    const { events, matchEvents, eventsStatus, matchEventsStatus } = this.props;
    const { label } = this.state;

    if (
      eventsStatus === STATUS_SUCCESS &&
      matchEventsStatus === STATUS_SUCCESS
    ) {
      return this.sortEvents([...events, ...matchEvents], label);
    } else return <Loading isWhite />;
  };

  render() {
    const {
      currentMonth,
      eventFilters,
      selectedEventFilters,
      teamFilters,
      federalFilters,
      selectedFederalFilters,
      selectedTeamFilters,
      label,
    } = this.state;

    return (
      <Fragment>
        <section className="banner banner--bigNoAfter">
          <div
            className="banner__thumbnail img-blue-dark"
            style={{ backgroundImage: `url(${getPublicAssets('/img/banner/gerer-mon-club.jpg')})` }}
          />
          <div className="container">
            <Breadcrumb elements={[{ name: 'accueil', to: '/' }, 'Événements']} isDark />
            <h1 className="ft-h2">Événements</h1>
            <p className="banner__desc">
            {'Retrouvez l\'ensemble des évènements des équipes de France et de la Fédération.'}</p>
          </div>
        </section>
        <div className="container">
          <div className="row mb-6 mb-md-7 mb-lg-8">
            <div className="col">
              <div className="box events events--game">
                <EventListNavbar
                  handleResetFilters={this.handleResetFilters}
                  handleMonthChanged={this.handleCurrentMonthChanged}
                  currentMonth={currentMonth}
                  handleLinkClicked={this.setLabel}
                  eventFilters={eventFilters}
                  handleEventFilterClicked={this.handleEventFilterClicked}
                  selectedEventFilters={selectedEventFilters}
                  teamFilters={teamFilters}
                  federalFilters={federalFilters}
                  selectedFederalFilters={selectedFederalFilters}
                  handleFederalFilterClicked={this.handleFederalFilterClicked}
                  handleTeamFilterClicked={this.handleTeamFilterClicked}
                  selectedTeamFilters={selectedTeamFilters}
                  labels={[LABEL_CALENDAR_TEAM, LABEL_CALENDAR_INSTITUTIONNEL]}
                  label={label}
                />
                {this.renderEvents()}
                <EventListPagination
                  currentMonth={currentMonth}
                  handleDecrementCurrentMonth={this.handleDecrementCurrentMonth}
                  handleIncrementCurrentMonth={this.handleIncrementCurrentMonth}
                />
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }
}

export default withHelmet(EventList)((Helmet, pageProps) => {
  const meta = getMetaBySlug('evenements');
  let { month, year } = pageProps.match.params;

  const description = meta.description(pageProps.ligue, month, year);
  const title = meta.title(pageProps.ligue, month, year);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name={'description'} content={description} />
      <meta property={'og:title'} content={title} />
      <meta property={'og:description'} content={description} />
      <meta property={'og:image'} content={getPublicAssets('/img/banner/gerer-mon-club.jpg')} />
      <meta property={'og:image:url'} content={getPublicAssets('/img/banner/gerer-mon-club.jpg')} />
      <link rel={'canonical'} href={window.location.href} />
    </Helmet>
  );
});
